import {
  CommonHelperFunctions,
  IProjectDto,
  IProjectGenerateRequestDTO,
  IProjectGenerationResponseDTO,
  IProjectsResponseDto,
  IProjectValidationRunResponseDTO,
  IRoleDto,
  IScreenDesignInfoDto,
  IWorkspaceProjectRole
} from "@kuika/kuika-cl-model";
import { IProjectCountsDto, ITemplateProjectDto } from "@kuika/kuika-cl-model/src/dtos/projects-response";
import Axios, { AxiosResponse } from "axios";
import { Patch } from "generate-json-patch";
import { FireBaseService } from "../firebase/firebase";
import { KMainFunctions } from "../shared/utility/main-functions";
import codeGenSseService from "./codegen-sse-service";
import { UserService } from "./user-service";

declare let window: any;

const backendUrl = CommonHelperFunctions.GetBackendUrl();

export class ProjectService {
  public static async Create(project: IProjectDto): Promise<AxiosResponse<IProjectDto>> {
    const res = await Axios.post<IProjectDto, AxiosResponse<IProjectDto>>(`${backendUrl}/projects/create`, project);
    const firebase = new FireBaseService();
    firebase.logEvent("project_created");
    // Fire-and-forget: don't block navigation on user info refresh
    UserService.GetUserInfoForce().catch(() => {});
    return res;
  }

  public static async GenerateProjectForInitialization(
    projectId: string
  ): Promise<AxiosResponse<IProjectsResponseDto[]>> {
    try {
      if (!window.kuika) {
        window.kuika = {};
      }
      window.kuika.activeProjectId = projectId;
      const res = await Axios.get<null, AxiosResponse>(`${backendUrl}/projects/generateforinitialization`);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetProjectRoleList(): Promise<AxiosResponse<IWorkspaceProjectRole[]>> {
    try {
      const res = await Axios.get<null, AxiosResponse>(`${backendUrl}/authorization/projects/roles`);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetAll(): Promise<AxiosResponse<IProjectsResponseDto[]>> {
    try {
      const res = await Axios.get<null, AxiosResponse>(`${backendUrl}/projects/getall`);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetAllProjectNames(): Promise<AxiosResponse<{ id: string; name: string }[]>> {
    try {
      const res = await Axios.get<null, AxiosResponse>(`${backendUrl}/projects/names`);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  // Workspace role registry (KOA-18634): extend an existing app role (its canonical id + name)
  // into another project. Server-side: tenant-scoped, idempotent by role name, branch-safe.
  public static async ExtendRoleToProject(projectId: string, roleId: string, roleName: string): Promise<AxiosResponse> {
    try {
      return await Axios.post<null, AxiosResponse>(`${backendUrl}/projects/${projectId}/roles`, {
        roleId,
        roleName
      });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  // Workspace role registry (KOA-18634): remove a role (by name) from a project's roles[].
  public static async RemoveRoleFromProject(projectId: string, roleName: string): Promise<AxiosResponse> {
    try {
      return await Axios.post<null, AxiosResponse>(`${backendUrl}/projects/${projectId}/roles/remove`, {
        roleName
      });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetRecentProjects(): Promise<AxiosResponse<IProjectsResponseDto[]>> {
    try {
      const res = await Axios.get<null, AxiosResponse>(`${backendUrl}/projects/getrecentprojects`);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  /**
   * The same list for a surface that only decorates — the header's app switcher. A failure
   * resolves to `null` instead of raising the error dialog: the switcher then lists nothing,
   * and its "Manage all apps" link still leads to the page that reports the error properly.
   */
  public static async GetRecentProjectsQuietly(): Promise<IProjectsResponseDto[] | null> {
    try {
      const res: AxiosResponse<IProjectsResponseDto[]> = await Axios.get<IProjectsResponseDto[]>(
        `${backendUrl}/projects/getrecentprojects`
      );
      return Array.isArray(res.data) ? res.data : null;
    } catch {
      return null;
    }
  }

  public static async GetMyProjects(): Promise<AxiosResponse<IProjectsResponseDto[]>> {
    try {
      const res = await Axios.get<null, AxiosResponse>(`${backendUrl}/projects/getmyprojects`);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetFavoriteProjects(): Promise<AxiosResponse<IProjectsResponseDto[]>> {
    try {
      const res = await Axios.get<null, AxiosResponse>(`${backendUrl}/projects/getfavoriteprojects`);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetBackups(): Promise<AxiosResponse<IProjectsResponseDto[]>> {
    try {
      const res = await Axios.get<null, AxiosResponse>(`${backendUrl}/projects/getbackups`);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  // Cheap My Apps / Favorites badge counts — two COUNT queries, not full lists.
  public static async GetProjectCounts(): Promise<AxiosResponse<IProjectCountsDto>> {
    try {
      const res = await Axios.get<null, AxiosResponse>(`${backendUrl}/projects/counts`);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async SetProjectFavoriteStatus(projectId: string, favoriteStatus: boolean): Promise<AxiosResponse> {
    try {
      return await Axios.post<IProjectDto, AxiosResponse>(`${backendUrl}/projects/projectfavoritestatus`, {
        projectId,
        favoriteStatus
      });
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async Delete(id: string): Promise<AxiosResponse> {
    try {
      const res = await Axios.delete<null>(`${backendUrl}/projects/${id}`);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async Get(id: string): Promise<AxiosResponse<IProjectDto>> {
    try {
      const res = await Axios.get<null, AxiosResponse>(`${backendUrl}/projects/${id}`);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetProjectJSONById(id: string): Promise<AxiosResponse<string>> {
    try {
      const res = await Axios.get<null, AxiosResponse>(`${backendUrl}/projects/${id}/json`);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GenerateSchema(projectName: string): Promise<string> {
    try {
      let payload = { projectName };
      const res = await Axios.post<null, AxiosResponse<any>>(`${backendUrl}/projects/generate-project-schema`, payload);
      return res.data.schemaName;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async Update(id: string, project: IProjectDto): Promise<boolean> {
    try {
      await Axios.put<IProjectDto, IProjectDto>(`${backendUrl}/projects/${id}`, project);
      return true;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return false;
    }
  }

  // public static async UpdateJson(updateObj: any): Promise<AxiosResponse> {
  //   try {
  //     const res = await Axios.put<null, AxiosResponse>(`${backendUrl}/projects/json`, updateObj);
  //     return res;
  //   } catch (error: any) {
  //     KMainFunctions.exceptionHandler(error);
  //     return error;
  //   }
  // }

  public static async PatchJson(patch: Patch, projectId: string): Promise<AxiosResponse> {
    try {
      const res = await Axios.patch<null, AxiosResponse>(`${backendUrl}/projects/${projectId}/json`, patch);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetProjectsWhichIHaveEditAccess(componentSetId): Promise<AxiosResponse<IProjectDto[]>> {
    try {
      const res = await Axios.get<null, AxiosResponse>(`${backendUrl}/projects/whichihaveeditaccess/${componentSetId}`);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  private static calcHasLocalSsl = (): boolean => {
    if (
      window.location?.hostname?.toLowerCase() === "localhost" &&
      window.location?.protocol?.toLowerCase() === "https:"
    ) {
      return true;
    }
    return false;
  };

  public static async GenerateProject(
    obj: IProjectGenerateRequestDTO
  ): Promise<AxiosResponse<IProjectGenerationResponseDTO>> {
    try {
      const hasLocalSsl = this.calcHasLocalSsl();
      obj.hasLocalSsl = hasLocalSsl;
      return await Axios.post<null, AxiosResponse>(`${backendUrl}/projects/generateproject`, obj);
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GenerateProjectViaSse(obj: IProjectGenerateRequestDTO) {
    await codeGenSseService.invokeFunction(JSON.stringify(obj));
  }

  public static async GetScreenList(projectId: string): Promise<AxiosResponse<IScreenDesignInfoDto[]>> {
    try {
      const res = await Axios.get<null, AxiosResponse>(`${backendUrl}/projects/screenlist/${projectId}`);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async Duplicate(obj: any): Promise<AxiosResponse> {
    try {
      const res = await Axios.post<null>(`${backendUrl}/projects/duplicate`, obj);
      await UserService.GetUserInfoForce();
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetAllRoles(): Promise<AxiosResponse<IRoleDto[]>> {
    try {
      const res = await Axios.get<null, AxiosResponse>(`${backendUrl}/projects/roles`);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetTemplateProjects(limit: number): Promise<AxiosResponse<ITemplateProjectDto[]>> {
    try {
      let url = `${backendUrl}/projects/templates`;
      if (limit && limit > 0) {
        url = `${backendUrl}/projects/templates/${limit}`;
      }
      const res = await Axios.get<null, AxiosResponse>(url);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  public static async GetManagedDbIdOfProject(projectId: string): Promise<AxiosResponse<string>> {
    try {
      const res = await Axios.get<null, AxiosResponse>(`${backendUrl}/projects/managed-db-id/${projectId}`);
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }

  /**
   * Standalone project validation — the same engine Preview runs before codegen, without
   * generating anything (Issues panel validate-on-open). Heavy (~18s typical on large
   * projects); the server single-flights concurrent calls per project and serves a short
   * cache, so repeated calls are safe. The project is the ambient currentProjectId header.
   * NEVER aborted client-side: the server completes and caches the run regardless, so an
   * abandoned popover still warms the next open.
   */
  public static async RunProjectValidation(): Promise<AxiosResponse<IProjectValidationRunResponseDTO>> {
    return Axios.post<IProjectValidationRunResponseDTO, AxiosResponse<IProjectValidationRunResponseDTO>>(
      `${backendUrl}/projectvalidation/run`,
      null
    );
  }

  public static async SetProjectHomeScreen(projectId: string, screenId: string): Promise<AxiosResponse> {
    try {
      const res = await Axios.post<null, AxiosResponse>(
        `${backendUrl}/projects/set-project-home-screen?projectId=${projectId}&screenId=${screenId}`
      );
      return res;
    } catch (error: any) {
      KMainFunctions.exceptionHandler(error);
      return error;
    }
  }
}
